import React, {memo} from "react"
import {useTranslation} from "react-i18next";

import FormSection from "../../../components/FormSection";
import {formikPropertyChanged} from "./index";
import CollectionsTabList from "./CollectionsTabList";
import Model23MeasureField from "./Model23MeasureField";
import Model23Select from "./Model23Select";
import {Grid} from "@mui/material";
import MDButton from "../../../materialDashboard/components/MDButton";
import WithInfoTooltip from "./WithInfoTooltip";


const PropagationSawTests = memo(({formik, model23Registry, emptyTest, inputDisabled}) => {
    const {t} = useTranslation(['models23', 'enums']);

    const onTestsUpdate = (tests) => {
        formik.setFieldValue('propagationSawTests', tests);
    }

    const renderTest = (testIndex) => {
        return <PropagationSawTest formik={formik}
                                   testIndex={testIndex}
                                   model23Registry={model23Registry}
                                   inputDisabled={inputDisabled}/>
    }

    const noTests = formik.values.propagationSawTests?.length === 0;

    const handleNewTest = () => {
        onTestsUpdate([...formik.values.propagationSawTests, emptyTest]);
    }

    return <FormSection title={t('model23Form.propagationSawTests')}
                        openDefault={false}
                        error={Boolean(formik.errors.propagationSawTests) && formik.touched.propagationSawTests}>
        <CollectionsTabList collections={formik.values.propagationSawTests}
                            renderCollection={renderTest}
                            emptyCollection={emptyTest}
                            onCollectionsUpdate={onTestsUpdate}
                            collectionType="test"
                            inputDisabled={inputDisabled}/>
        {
            !inputDisabled && noTests && <MDButton variant="text"
                                                   color="info"
                                                   onClick={handleNewTest}
                                                   sx={{display: 'block', ml: 'auto'}}>
                {t('collectionsMenu.addTest')}
            </MDButton>
        }
    </FormSection>
}, (prevProps, nextProps) => formikPropertyChanged(prevProps, nextProps, 'propagationSawTests') && prevProps.model23Registry === nextProps.model23Registry);


const PropagationSawTest = ({formik, testIndex, model23Registry, inputDisabled}) => {
    const pstAttributesRegistry = model23Registry.data?.propagationSawTestAttributes;
    const pstRegistry = model23Registry.data?.propagationSawTestRegistry;
    const pstCodeRegistry = pstRegistry?.pstCode;
    const fractureTypeRegistry = pstRegistry?.fractureType;

    const testPrefix = `propagationSawTests[${testIndex}]`

    return <Grid container rowSpacing={3} columnSpacing={4}>
        <Grid item xs={12} md={6}>
            <WithInfoTooltip message={pstAttributesRegistry?.pstCode?.description}>
                <Model23Select name={`${testPrefix}.pstCode`}
                               label={pstAttributesRegistry?.pstCode?.name}
                               labelFn={r => `${r.code} - ${r.description}`}
                               formik={formik}
                               registry={pstCodeRegistry}
                               disabled={inputDisabled}/>
            </WithInfoTooltip>
        </Grid>
        <Grid item xs={12} md={6}>
            <WithInfoTooltip message={pstAttributesRegistry?.fractureType?.description}>
                <Model23Select name={`${testPrefix}.fractureType`}
                               label={pstAttributesRegistry?.fractureType?.name}
                               labelFn={r => `${r.code} - ${r.description}`}
                               formik={formik}
                               registry={fractureTypeRegistry}
                               disabled={inputDisabled}/>
            </WithInfoTooltip>
        </Grid>
        <Grid item xs={12} md={6}>
            <WithInfoTooltip message={pstAttributesRegistry?.cutLength?.description}>
                <Model23MeasureField formik={formik}
                                     label={pstAttributesRegistry?.cutLength?.name}
                                     name={`${testPrefix}.cutLength`}
                                     unitOfMeasure="cm"
                                     type="number"
                                     disabled={inputDisabled}/>
            </WithInfoTooltip>
        </Grid>
        <Grid item xs={12} md={6}>
            <WithInfoTooltip message={pstAttributesRegistry?.columnLength?.description}>
                <Model23MeasureField formik={formik}
                                     label={pstAttributesRegistry?.columnLength?.name}
                                     name={`${testPrefix}.columnLength`}
                                     unitOfMeasure="cm"
                                     type="number"
                                     disabled={inputDisabled}/>
            </WithInfoTooltip>
        </Grid>

        <Grid item xs={12} md={6}>
            <WithInfoTooltip message={pstAttributesRegistry?.depth?.description}>
                <Model23MeasureField formik={formik}
                                     label={pstAttributesRegistry?.depth?.name}
                                     name={`${testPrefix}.depth`}
                                     unitOfMeasure="cm"
                                     type="number"
                                     disabled={inputDisabled}/>
            </WithInfoTooltip>
        </Grid>
        <Grid item xs={12} md={6}>
            <WithInfoTooltip message={pstAttributesRegistry?.tdh?.description}>
                <Model23MeasureField formik={formik}
                                     label={pstAttributesRegistry?.tdh?.name}
                                     name={`${testPrefix}.tdh`}
                                     unitOfMeasure="cm"
                                     type="number"
                                     disabled={inputDisabled}/>
            </WithInfoTooltip>
        </Grid>
    </Grid>
}

export default PropagationSawTests;